import React, { useEffect } from 'react';
import { CheckCircleIcon, TagIcon } from '@heroicons/react/24/outline';
import { notifyOrgContactTagsUpdated } from '../../../../utils/orgContactTagsEvents';
import type { ContactTagsApplyResultPayload } from '../types';

// Success card for a completed chat-driven tag apply (the assistant's
// apply_contact_tags tool). Also fires the org-tags-updated event once per
// result so Settings → Tags refreshes live (new tag / usage counts) even when
// the write happened server-side.
const notifiedResultKeys = new Set<string>();

export function ContactTagsApplyResultCard({
  payload,
}: {
  payload: ContactTagsApplyResultPayload;
}) {
  const resultKey = `${payload.appliedAtIso || ''}:${payload.action}:${(payload.tags || []).join(',')}:${payload.updated}`;

  useEffect(() => {
    if (notifiedResultKeys.has(resultKey)) return;
    notifiedResultKeys.add(resultKey);
    notifyOrgContactTagsUpdated();
  }, [resultKey]);

  const verb = payload.action === 'remove' ? 'Removed' : 'Added';
  const preposition = payload.action === 'remove' ? 'from' : 'to';
  const createdTags = payload.createdTags || [];

  return (
    <div className="w-full rounded-lg border border-sage/40 bg-sage-light/30 p-4 text-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage">
          <CheckCircleIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink">
            {verb}{' '}
            <span className="inline-flex flex-wrap items-center gap-1 align-middle">
              {(payload.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full border border-sage/40 bg-white px-2 py-0.5 text-xs font-medium text-sage"
                >
                  <TagIcon className="h-3 w-3" />
                  {tag}
                </span>
              ))}
            </span>{' '}
            {preposition} {payload.updated.toLocaleString()} contact
            {payload.updated === 1 ? '' : 's'}
          </div>
          <p className="mt-1 text-xs text-ink-secondary">
            {payload.unchanged > 0
              ? `${payload.unchanged.toLocaleString()} already ${payload.action === 'remove' ? 'did not have it' : 'had it'} · `
              : ''}
            {payload.skipped > 0 ? `${payload.skipped.toLocaleString()} skipped · ` : ''}
            {(payload.excludedCount || 0) > 0
              ? `${(payload.excludedCount || 0).toLocaleString()} excluded by you · `
              : ''}
            {payload.criteriaEcho || ''}
          </p>
          {createdTags.length > 0 && (
            <p className="mt-1 text-xs text-ink-muted">
              Created new tag{createdTags.length === 1 ? '' : 's'}: {createdTags.join(', ')} — now
              in your tag list.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
